import type { CrmStateField, SaleCrmStateHistoryEntry } from 'services/types/sale';

// Salesforce stores every field as text, so the type it should be read back as
// travels with the label instead of being inferred from the value.
export type CrmFieldFormat = 'text' | 'currency' | 'integer' | 'boolean' | 'date' | 'dateTime' | 'url';

/**
 * Label and format for every field the backend maps onto `crmState`. A `Record`
 * over `CrmStateField` (not an array) so adding a field on the backend without
 * giving it a label here fails to compile.
 */
export const CRM_FIELD_CATALOG: Record<CrmStateField, { label: string; format: CrmFieldFormat }> = {
  stage: { label: 'Etapa', format: 'text' },
  subStage: { label: 'Subetapa', format: 'text' },
  stageChangedAt: { label: 'Último cambio de etapa', format: 'dateTime' },
  statusJustification: { label: 'Justificación', format: 'text' },
  operationNumber: { label: 'N° de operación', format: 'text' },
  ownerId: { label: 'Owner (Salesforce)', format: 'text' },
  hasError: { label: 'Oportunidad con error', format: 'boolean' },
  documentsStatus: { label: 'Estado documentos', format: 'text' },
  documentsRejectionReason: { label: 'Motivo de rechazo', format: 'text' },
  documentsApprovedAt: { label: 'Antecedentes OK', format: 'date' },
  mandateSignatureStatus: { label: 'Firma del mandato', format: 'text' },
  mandateAssignedAt: { label: 'Mandato asignado', format: 'date' },
  mandateUrl: { label: 'Link del mandato', format: 'url' },
  eCertDocumentId: { label: 'ID documento eCert', format: 'text' },
  reviewerName: { label: 'Ejecutivo de revisión', format: 'text' },
  portabilityApproved: { label: 'Portabilidad aprobada', format: 'boolean' },
  portabilityKey: { label: 'Clave de portabilidad', format: 'text' },
  portabilityType: { label: 'Tipo de portabilidad', format: 'text' },
  qualifies: { label: 'Califica', format: 'boolean' },
  cancellationStatus: { label: 'Estado de renuncia', format: 'text' },
  cancellationSent: { label: 'Renuncia enviada', format: 'boolean' },
  cancellationSentAt: { label: 'Fecha envío renuncia', format: 'date' },
  cancellationUpdatedAt: { label: 'Renuncia actualizada', format: 'dateTime' },
  originInsurerName: { label: 'Compañía de origen', format: 'text' },
  originInsurerRut: { label: 'RUT compañía de origen', format: 'text' },
  originFinancialInstitution: { label: 'Institución financiera de origen', format: 'text' },
  originInstitution: { label: 'Institución de origen', format: 'text' },
  originInstitutionType: { label: 'Tipo de institución', format: 'text' },
  originPolicyNumber: { label: 'N° de póliza original', format: 'text' },
  originInsuranceAmount: { label: 'Valor seguro original', format: 'currency' },
  policyAmended: { label: 'Póliza rectificada', format: 'boolean' },
  newInsurerName: { label: 'Nueva compañía', format: 'text' },
  newInsurerRut: { label: 'RUT nueva compañía', format: 'text' },
  newInsuranceStatus: { label: 'Estado nuevo seguro', format: 'text' },
  newInsuranceAmount: { label: 'Valor nuevo seguro', format: 'currency' },
  policyIssuedAt: { label: 'Emisión de póliza', format: 'date' },
  coverageEndsAt: { label: 'Fin de vigencia', format: 'date' },
  policiesUpdated: { label: 'Pólizas actualizadas', format: 'boolean' },
  unemploymentAssistance: { label: 'Asistencia desempleo', format: 'currency' },
  requestedAmountOriginal: { label: 'Monto solicitado original', format: 'currency' },
  amountDue: { label: 'Monto por pagar', format: 'currency' },
  installmentsDue: { label: 'Cuotas por pagar', format: 'integer' },
  installmentsPending: { label: 'Cuotas pendientes', format: 'integer' },
  loanBalance: { label: 'Saldo del crédito', format: 'currency' },
  refundAmountSimulated: { label: 'Devolución simulada', format: 'currency' },
  refundAmountFinal: { label: 'Devolución final', format: 'currency' },
  payoutAmountFinal: { label: 'Abono final', format: 'currency' },
  insurerReportedAmount: { label: 'Monto informado por la compañía', format: 'currency' },
  unusedPremiumRefundApprox: { label: 'Prima no consumida (aprox.)', format: 'currency' },
  unemploymentPremiumAmount: { label: 'Prima cesantía', format: 'currency' },
  totalDiscounts: { label: 'Total de descuentos', format: 'currency' },
  clientCreditBalanceApprox: { label: 'Saldo a favor del cliente (aprox.)', format: 'currency' },
  operatingCost: { label: 'Costo operativo', format: 'currency' },
  paymentStatus: { label: 'Estado del pago', format: 'text' },
  clientPaymentStatus: { label: 'Estado pago cliente', format: 'text' },
  clientPayoutAt: { label: 'Fecha de abono al cliente', format: 'date' },
  paymentEstimatedAt: { label: 'Fecha estimada de pago', format: 'date' },
  // Salesforce sends a numeric bank code here, not a name, and no code→name
  // table exists on our side — label it as a code rather than mislabel it.
  clientBankName: { label: 'Banco del cliente (código)', format: 'text' },
  clientBankAccountNumber: { label: 'N° de cuenta', format: 'text' },
  clientBankAccountType: { label: 'Tipo de cuenta', format: 'text' },
  simulationRecalculated: { label: 'Simulación recalculada (CRM)', format: 'boolean' },
};

/** The fields worth reading at a glance — always rendered, even when empty. */
export const CRM_PRIMARY_FIELDS: readonly CrmStateField[] = [
  'operationNumber',
  'stage',
  'subStage',
  'documentsStatus',
  'documentsRejectionReason',
  'mandateSignatureStatus',
  'mandateUrl',
  'newInsuranceStatus',
  'cancellationStatus',
  'statusJustification',
  'paymentStatus',
  'clientPayoutAt',
];

/**
 * The rest, as collapsible subsections: flat, the ~60 mapped fields would be
 * some thirty grid rows drowning a card that otherwise shows about twenty.
 */
export const CRM_FIELD_GROUPS: ReadonlyArray<{ title: string; fields: readonly CrmStateField[] }> = [
  {
    title: 'Montos',
    fields: [
      'requestedAmountOriginal',
      'loanBalance',
      'amountDue',
      'installmentsDue',
      'installmentsPending',
      'refundAmountSimulated',
      'refundAmountFinal',
      'payoutAmountFinal',
      'insurerReportedAmount',
      'unusedPremiumRefundApprox',
      'unemploymentPremiumAmount',
      'totalDiscounts',
      'clientCreditBalanceApprox',
      'operatingCost',
    ],
  },
  {
    title: 'Documentos y mandato',
    fields: ['reviewerName', 'documentsApprovedAt', 'mandateAssignedAt', 'eCertDocumentId'],
  },
  {
    title: 'Portabilidad y renuncia',
    fields: [
      'portabilityType',
      'portabilityApproved',
      'portabilityKey',
      'qualifies',
      'cancellationSent',
      'cancellationSentAt',
      'cancellationUpdatedAt',
    ],
  },
  {
    title: 'Seguro de origen',
    fields: [
      'originInsurerName',
      'originInsurerRut',
      'originInstitution',
      'originInstitutionType',
      'originFinancialInstitution',
      'originPolicyNumber',
      'originInsuranceAmount',
      'policyAmended',
    ],
  },
  {
    title: 'Nuevo seguro',
    fields: [
      'newInsurerName',
      'newInsurerRut',
      'newInsuranceAmount',
      'policyIssuedAt',
      'coverageEndsAt',
      'policiesUpdated',
      'unemploymentAssistance',
    ],
  },
  {
    title: 'Pago al cliente',
    fields: [
      'clientPaymentStatus',
      'paymentEstimatedAt',
      'clientBankName',
      'clientBankAccountType',
      'clientBankAccountNumber',
    ],
  },
  {
    title: 'Otros',
    fields: ['stageChangedAt', 'hasError', 'simulationRecalculated', 'ownerId'],
  },
];

// Flat render order, so a history entry lists its changes the way the grid orders them.
export const CRM_FIELD_ORDER: readonly CrmStateField[] = [
  ...CRM_PRIMARY_FIELDS,
  ...CRM_FIELD_GROUPS.flatMap((group) => group.fields),
];

/**
 * Formats a `date`-format field. Salesforce sends those as a bare `YYYY-MM-DD`,
 * which `new Date` reads as UTC midnight — formatted in a negative-offset locale
 * that lands on the previous day, so pin it to local midnight first.
 */
export const formatCrmDateOnly = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * A history entry's changed fields in catalog order. Keys the catalog doesn't
 * know are appended under their raw name rather than dropped: the backend maps
 * fields this list has to mirror by hand, and a missed one would leave an entry
 * showing nothing but its timestamp.
 */
export const orderCrmChangeKeys = (changes: SaleCrmStateHistoryEntry['changes']): string[] => [
  ...CRM_FIELD_ORDER.filter((key) => key in changes),
  ...Object.keys(changes).filter((key) => !(key in CRM_FIELD_CATALOG)),
];
